import { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";

interface Option {
  value: string;
  label: string;
}

interface SelectProps {
  options: Option[];
  placeholder?: string;
  onChange: (value: string) => void;
  className?: string;
  defaultValue?: string;
  value?: string;
  readOnly?: boolean;
  dropUp?: boolean;
  theme?: "light" | "dark"; // NOVO
}

const Select: React.FC<SelectProps> = ({
  options,
  placeholder = "Selecione uma opção",
  onChange,
  className = "",
  defaultValue = "",
  value,
  readOnly = false,
  dropUp = false,
  theme,
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(defaultValue);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value !== undefined) setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Verificar se o dropdown está aberto antes de tentar fechá-lo
      if (isOpen && selectRef.current && !selectRef.current.contains(event.target as Node)) {
        // Use requestAnimationFrame para garantir que não haja colisão com outros eventos
        requestAnimationFrame(() => {
          setIsOpen(false);
        });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelectClick = () => { if (!readOnly) setIsOpen(!isOpen); };
  const handleOptionClick = (optionValue: string, event?: React.MouseEvent) => {
    if (readOnly) return;
    
    // Impedir propagação do evento para evitar que o handleClickOutside seja acionado
    event?.stopPropagation();
    
    setSelectedValue(optionValue);
    onChange(optionValue);
    
    // Use requestAnimationFrame para garantir que o estado é atualizado corretamente
    requestAnimationFrame(() => {
      setIsOpen(false);
    });
  };
  const selectedLabel = selectedValue
    ? options.find(option => option.value === selectedValue)?.label || selectedValue
    : placeholder;

  // ==============================
  // CLASSES DINÂMICAS
  // ==============================
  // Campo do select
  let selectClasses = "h-11 w-full rounded-lg border px-4 py-2.5 pr-11 text-sm shadow-theme-xs cursor-pointer flex items-center justify-between focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 ";
  // Label do valor selecionado
  let labelColor = "";
  // Chevron
  let chevronColor = "transition-transform duration-200 ";
  // Dropdown de opções
  let dropdownClasses = "absolute z-10 w-full rounded-lg shadow-lg mt-1 max-h-60 overflow-y-auto ";

  // LIGHT MODE
  if (theme === "light") {
    selectClasses += "border-gray-200 bg-white ";
    selectClasses += selectedValue ? "text-gray-800 " : "text-gray-400 ";
    selectClasses += readOnly ? "cursor-not-allowed opacity-75 " : "";
    labelColor = selectedValue ? "text-gray-800" : "text-gray-400";
    chevronColor += "text-gray-500";
    dropdownClasses += "bg-white border border-gray-300 ";
  }
  // DARK MODE
  else if (theme === "dark") {
    selectClasses += "border-gray-700 bg-gray-900 ";
    selectClasses += selectedValue ? "text-white/90 " : "text-gray-400 ";
    selectClasses += readOnly ? "cursor-not-allowed opacity-75 " : "";
    labelColor = selectedValue ? "text-white/90" : "text-gray-400";
    chevronColor += "text-gray-400";
    dropdownClasses += "bg-gray-900 border border-gray-700 ";
  }
  // PADRÃO (herda dark: do tema global)
  else {
    selectClasses += "border-gray-200 bg-transparent dark:border-gray-800 dark:bg-gray-900 ";
    selectClasses += selectedValue ? "text-gray-800 dark:text-white/90 " : "text-gray-400 dark:text-gray-400 ";
    selectClasses += readOnly ? "cursor-not-allowed opacity-75 " : "";
    labelColor = selectedValue ? "text-gray-800 dark:text-white/90" : "text-gray-400 dark:text-gray-400";
    chevronColor += "text-gray-500 dark:text-gray-400";
    dropdownClasses += "bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 ";
  }
  dropdownClasses += dropUp ? "bottom-full mb-1" : "top-full mt-1";

  return (
    <div className={`relative w-full ${className}`} ref={selectRef}>
      {/* Campo de seleção */}
      <div className={selectClasses} onClick={handleSelectClick}>
        <span className={labelColor}>{selectedLabel}</span>
        <ChevronDown
          size={18}
          className={`${chevronColor} ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </div>
      {/* Dropdown */}
      {isOpen && (
        <div className={dropdownClasses}>
          {options.map((option) => (
            <div
              key={option.value}
              className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${theme === "dark"
                ? "text-white/90 hover:bg-gray-800"
                : theme === "light"
                  ? "text-gray-700"
                  : "text-gray-700 dark:text-white/90 dark:hover:bg-gray-800"
                }`}
              onClick={(event) => handleOptionClick(option.value, event)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Select;
